import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import PageLoaderSpinner from './PageLoaderSpinner';

export const LAYOUT_CONTENT_SECTION_ID = 'layout-content-section';
export const LAYOUT_CONTENT_ID = 'layout-content';

type PageLoaderOverlayProps = {
    active: boolean;
    className?: string;
    label?: string;
};

function getPortalTarget() {
    return document.getElementById(LAYOUT_CONTENT_SECTION_ID) ?? document.body;
}

export default function PageLoaderOverlay({
    active,
    className,
    label = 'Loading',
}: PageLoaderOverlayProps) {
    useEffect(() => {
        if (!active) {
            return undefined;
        }

        const contentSection = document.getElementById(LAYOUT_CONTENT_SECTION_ID);
        const content = document.getElementById(LAYOUT_CONTENT_ID);

        if (contentSection) {
            const previousOverflow = contentSection.style.overflow;
            contentSection.style.overflow = 'hidden';
            content?.setAttribute('inert', '');
            return () => {
                contentSection.style.overflow = previousOverflow;
                content?.removeAttribute('inert');
            };
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        content?.removeAttribute('inert');
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [active]);

    useEffect(
        () => () => {
            const contentSection = document.getElementById(LAYOUT_CONTENT_SECTION_ID);
            if (contentSection) {
                contentSection.style.overflow = '';
                return;
            }
            document.body.style.overflow = '';
        },
        []
    );

    if (!active) {
        return null;
    }

    const portalTarget = getPortalTarget();
    const isContentSection = portalTarget.id === LAYOUT_CONTENT_SECTION_ID;

    return createPortal(
        <div
            className={cn(
                'z-[9999] flex items-center justify-center bg-white/50 backdrop-blur-[0.25rem]',
                isContentSection ? 'absolute inset-0' : 'fixed inset-0',
                className
            )}
            role='status'
            aria-live='polite'
            aria-busy='true'
            aria-label={label}
        >
            <div className='h-14 w-14 shrink-0 animate-[spin_0.85s_linear_infinite]'>
                <PageLoaderSpinner />
            </div>
        </div>,
        portalTarget
    );
}
